/*
   Copyright 2024, Guillermo Vega-Gorgojo

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/

import config from './data/config.json';
import dict from './data/dictionary.json';
import { updateHTMLtemplates } from './data/htmlTemplates.js';

import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css' 
import "leaflet/dist/leaflet.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'nouislider/dist/nouislider.css';
import 'shepherd.js/dist/css/shepherd.css';
import "./css/forexp.css";

import L from 'leaflet';
import bootstrap from "bootstrap/dist/js/bootstrap.bundle.min.js";
import JSONCrush from 'jsoncrush';

import { inicializarMapa, Mimapa } from './modules/map.js';
import { string2loc, loc2string, getLiteral, expandPrefix, applyPrefix, uriToLiteral, configurarModal } from './modules/util.js';
import { CraftsAPI, TextEngine } from './modules/queryInterface.js';
import { getUsesInfo, getTaxonsInfo, getProvincesInfo } from './modules/dataManager.js';
import { cargarPanel, cargarBotonesMapa, actualizarIdiomaPanel } from './modules/mapControls.js';
import { mapaMovido, finActualizarMapa, obtenerConfigMapaBase, actualizarMapaBase } from './modules/map.js';
import { inicializarProvincias, cargarProvincias, ajustarColorProvincias, ajustarPopupsProvincias } from './modules/provinces.js';
import { inicializarTeselas, ajustarColorTeselas, ajustarPopupsTeselas } from './modules/patches.js';
import { ajustarColorParcelas, ajustarPopupsParcelas } from './modules/plots.js';
import { ajustarColorArboles, ajustarPopupsArboles } from './modules/trees.js';
import { dashboardParcela } from './modules/dashboards.js';
import { renderEntradaLugares } from './modules/places.js';
import { visualizarFiltrosTaxon, correspondenciaTaxon, correspondenciaTaxonMulti } from './modules/taxons.js';
import { generaIconos } from './modules/icons.js';
import { lanzarTour } from './modules/shepherd.js';
import { getFirebaseApp, sendEvent } from './modules/events.js';

// variables globales
let Sesion, Datos, Layers, Crafts, Solr, mimodal;

// vamos a la inicialización
inicializar();

// INICIALIZACIÓN APLICACIÓN
async function inicializar() {
	// genero plantillas HTML
	updateHTMLtemplates();
	
	// modal de carga
	configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true, 
		spinnerMessage: getLiteral(dict.confdata)}, getLiteral(dict.starting), null, null);
	mimodal = new bootstrap.Modal(document.getElementById('mimodal'));
	mimodal.show();
	
	// INICIALIZO Layers
	Layers = {};
	
	// INICIALIZACIÓN SESIÓN
	Sesion = {};
	
	// inicializo número de páginas en la historia para controlar el ir hacia atrás
	Sesion.npags = 0;

	// inicializo timeouts
	Sesion.timeout = config.timeout;
	Sesion.huboTimeout = false;	

	// bloqueo para pintar mapa
	Sesion.actualizandoMapa = false; // bloqueo si se está actualizando el mapa
	Sesion.mapaMovido = false; // detecto si el mapa se movió para actualizar
	Sesion.idTimeoutActualizar = null; // id del timeout para actualización automática (para que no se bloquee)
	
	// no está dibujando el polígono de descarga
	Sesion.poligonero = false;

	// estado de la sesión (construido a partir de la URL)
	Sesion.estado = {};

	// inicialización zoom
	Sesion.zoom = undefined;
	Sesion.zoomUsados = {}; // guardo los zooms usados en el mapa para facilitar el cacheo

	// inicializo nombres científicos con el valor de nomci en local storage o true
	Sesion.nomci = localStorage.getItem('nomci') === "false"? false : true;

	// inicializo filtros de taxones
	Sesion.taxones = [];	
	Sesion.taxonesColores = [];
	Sesion.taxonesCorrExpuris = []; // guardo la correspondencia de las expuris de cada taxón a "ifn3" (necesario para las teselas)
	Sesion.taxonesFiltros = {}; // mapa, incluyendo el caso de treeUri
	// inicializo sugerencias taxones 
	Sesion.taxonFocus = -1;
	
	// progreso y estadísticos
	Sesion.infoCeldas = {}; // no hace falta inicializar, pero así queda claro su uso

	// inicializo las áreas mínimas de las teselas para cada nivel de zoom
	Sesion.tesAreaminZoom = {};
	// inicializo las teselas pintadas
	Sesion.tesPintadas = {};
	Sesion.tesPopupsRegenerar = {}; // para que no tarde tanto la actualización de popups
	
	// inicializo las parcelas pintadas
	Sesion.parPintadas = {};
	Sesion.parPintadasMulti = {}; // marco las parcelas con múltiples secciones
	Sesion.parPopupsRegenerar = {}; // para que no tarde tanto la actualización de popups
	Sesion.radioParPintadas = config.radioParcela; // 300 metros de radio por defecto
	
	// inicializo los árboles pintados
	Sesion.arbPintados = {};
	// inicializo las parcelas en zoom de árbol pintadas
	Sesion.parcarbPintadas = {};

	// inicializo índices de colores para las ediciones del IFN disponibles (dashboard)
	Sesion.colifnind = {};
	for (const ifnev of config.ifnVersions) {
		// colores disponibles
		const cols = config.coltxinds.filter(col => !Object.values(Sesion.colifnind).includes(col));
		const ind = Math.floor(Math.random() * cols.length);
		Sesion.colifnind[ifnev] = cols[ind];
	}	

	// INICIALIZACIÓN DATOS
	Datos = {};
	Datos.taxones = {};
	Datos.usos = {};
	Datos.provs = {};
	Datos.arboles = {};
	Datos.parcelas = {};
	Datos.parcinfos = {}; // para los dashboards de parcelas
	Datos.teselas = {};
	Datos.ladosCeldas = {}; // aquí calculo el lado de cada celda por nivel de zoom
	Datos.celdasTeselas = {}; // aquí incluyo las IRIs de las teselas en cada celda
	Datos.celdasParcelas = {}; // aquí incluyo las IRIs de las teselas en cada celda
	
	
	// CARGA INICIAL DE LA URL
	// 1) carga localización si la hay
	// 2) carga taxones sin comprobar existencia
	// 3) ajusta vista
	// 4) no hace nada en el mapa (que no está aún creado)
	cargarURL(true);
				
	// SI NO HAY POSICIÓN INICIAL Y ESTÁ EN MODO MAPA, CARGO LA DE LA CONFIGURACIÓN Y PEDIRÉ GEOPOSICIONAR
	let pedirgeopos = false;
	if (!Sesion.estado.loc) { // precargo localización de la configuración
		Sesion.estado.loc = {
			lat: config.geolocstart[0],
			lng: config.geolocstart[1],
			z: config.zStart
		};
		if (Sesion.estado.path === 'map' && !localStorage.getItem('tourCompletado')) // pido geopos si está en modo mapa y no ha hecho el tour
			pedirgeopos = true;
	}
	
	// INICIALIZO MAPA
	inicializarMapa();
		
	// INCLUYO BOTONES DEL MAPA
	cargarBotonesMapa();
		
	// INICIALIZACIÓN FIREBASE
	getFirebaseApp();
	
	// INICIALIZACIÓN CRAFTS
	console.time("Configuración proveedor de datos");
	Crafts = new CraftsAPI(config.craftsConfig);
	Crafts.test()
		.then( () => {			
			// OK
			console.info("Proveedor de datos funciona");		
			console.timeEnd("Configuración proveedor de datos");
			
			// inicializo la caché sin esperar a que termine de indexar
			Crafts.initCache();
						
			// CARGO PANEL (inicialmente desactivado el filtro de taxón)
			cargarPanel();
			
			// CONFIGURACIÓN SOLR
			configurarSolr();
						
			// TODO: ¿hacer lo del puppeteer?			
						
			// DETECCIÓN DE CAMBIOS EN EL MAPA
			Mimapa.on('moveend', mapaMovido);
			Sesion.listenerMoveend = true; // para ajustar localización si hay que inicializar a la posición del usuario
				
			// INICIALIZACIONES CRAFTS
			console.group("Inicialización de datos");
						
			// obtengo datos de los usos para las teselas
			console.time("Carga de datos de usos");			
			getUsesInfo()
				.then(() => {
					console.info("Info de usos cargada");
					console.timeEnd("Carga de datos de usos");
				})
				.catch(error => console.error(error));

			// actualizo modal
			configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true, 
				spinnerMessage: getLiteral(dict.conftaxon)}, getLiteral(dict.starting), null, null);

			// obtengo datos de todos los taxones
			console.time("Carga de datos de taxones");
			getTaxonsInfo()
				.then(() => {
					console.info("Info de taxones cargada");
					console.timeEnd("Carga de datos de taxones");
					// habilito el botón de filtrar por taxón (desactivado hasta ahora)
					document.getElementById("bot_taxones").removeAttribute('disabled');
							
					// actualizo modal
					configurarModal({ static: true, vertcent: true, nofooter: true, spinner: true, 
						spinnerMessage: getLiteral(dict.confreg)}, getLiteral(dict.starting), null, null);
				
					// obtengo datos de las provincias (espero a tener los datos de especies para hacerlo)
					console.time("Carga de datos de provincias");		
					getProvincesInfo()
						.then(() => {
							console.info("Info de provincias cargada");
							console.timeEnd("Carga de datos de provincias");
							console.groupEnd();
							
							// escondo el modal (si no está mostrado pongo un listener para cerrarlo)							
							mimodal.hide();
							document.getElementById('mimodal').addEventListener('shown.bs.modal', function () {
      							mimodal.hide();
   							}, { once: true });
   							
							// cargo las provincias
							cargarProvincias();
											
							// AQUÍ SE HACE LA PRIMERA ACTUALIZACIÓN COMPLETA DEL MAPA
							cargarURL();
						})
						.catch(error => console.error(error));
				})
				.catch(error => console.error(error));	
		})
		.catch(error => {
			console.error(error);
			console.timeEnd("Configuración proveedor de datos");
			// aviso error
			errorProveedorDatosIrrecuperable(error);
		});
			
	
	// DETECCIÓN EVENTOS POPSTATE
	// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onpopstate
	window.onpopstate = function(event) {
		// cargo la URL
		cargarURL();
	};
	
	// DETECCIÓN CLICKS EN ENLACES NAVBAR Y BOTÓN SANDWICH (con event delegation)
	// DETECCIÓN CLICKS PARA DASHBOARD PARCELAS (con event delegation)
	document.addEventListener("click", function(event) {
		if (event.target.matches(".navbar-brand")) {
			e.preventDefault(); // Prevents the default behavior of the click event
		    e.stopPropagation(); // Stops the propagation of the click event
		}
		else if (event.target.matches(".nav-link, .dropdown-item")) {
			event.preventDefault(); // Prevent the default behavior of the hyperlink
        	// If the clicked element has the class "lang"
			if (event.target.classList.contains("lang")) {
				// recupero y actualizo idioma
				const nlang = event.target.getAttribute("tag");
				localStorage.setItem('lang', nlang);
				// selección idioma
				sendEvent( 'select_content', { content_type: 'lang_' + nlang } );
				// actualizo plantillas HTML
				updateHTMLtemplates();
				// actualizo etiquetas mapa
				actualizarIdiomaPanel();
				// y cargo la URL (para las páginas no mapa)
				if (Sesion.estado.path !== "map")				
					cargarURL();
			} // If the clicked element has the class "home"
			else if (event.target.classList.contains("home")) {
				// Redirect to the landing page
				window.location.href = getLiteral(dict.homelocrel); //'..';      
			} // If the clicked element has the class "map"
			else if (event.target.classList.contains("map")) {
				Sesion.estado.path = "map";
				delete Sesion.estado.etid;
				// actualizo etiquetas mapa
				actualizarIdiomaPanel();
				// actualizo mapa base
				actualizarMapaBase();
				// reajusto url y creo nueva página en la historia
				history.pushState(Sesion.estado, "", obtenerURL());
				Sesion.npags++; // una página 
				// cargoURL => me llevará al mapa
				cargarURL();
			}
		}
		else if (event.target.classList.contains("plot")) { // dashboard parcelas
			event.preventDefault(); // Prevent the default behavior of the hyperlink
			// voy al dashboard de la parcela
			Sesion.estado.path = "plot";
			Sesion.estado.etid = event.target.getAttribute("iri");
			// ajusto oifns para activar directamente la comparación
			Sesion.estado.oifns = {};
			for (const ifnev of config.ifnVersions) {
				if (event.target.getAttribute(ifnev) === 'true')
					Sesion.estado.oifns[ifnev] = true;					
			}		
			// reajusto url y creo nueva página en la historia
			history.pushState(Sesion.estado, "", obtenerURL());
			Sesion.npags++; // una página más
			// cargoURL => me llevará a dashboardParcela
			cargarURL();
		}
	});
		
	
	// INICIALIZO LayerGroup DE TESELAS
	Layers.tess = inicializarTeselas();	
	// INICIALIZO LayerGroup DE PROVINCIAS
	Layers.provs = inicializarProvincias();
	// INICIALIZO LayerGroup DE PARCELAS
	Layers.parcs = L.layerGroup().addTo(Mimapa);	
	// INICIALIZO LayerGroup DE ÁRBOLES
	Layers.arbs = L.layerGroup().addTo(Mimapa);
	
	// GENERO ICONOS
	generaIconos();
		
	// TIMESTAMP ACTUAL (para geoposición y para formulario)
	const ahora = Date.now();
		
	// VUELO A LA POSICIÓN DEL USUARIO SI QUIERE
	// sólo si usuario no dijo que no anteriormente o ha pasado el barbecho
	if (pedirgeopos && (!localStorage.getItem('timestampPedirLoc') || 
			Number(localStorage.getItem('timestampPedirLoc')) < ahora)) {
		navigator.geolocation.getCurrentPosition( function(pos) {
			// vamos a la localización del usuario
			Mimapa.flyTo([ pos.coords.latitude, pos.coords.longitude], config.zLugar, {animate: true, duration: 1});
			// reajusto la localización (si aún no se ha llamado al listener de 'moveend')
			if (!Sesion.listenerMoveend) {
				Sesion.estado.loc = {
					lat: pos.coords.latitude,
					lng: pos.coords.longitude,
					z: config.zLugar
				};					
			}
		}, function(err) {
			// pongo barbecho si el usuario dijo que no
			// err.code 1 => https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError
			if (err.code == 1) // barbecho
				localStorage.setItem('timestampPedirLoc', ahora + config.interSessionQGap); // barbecho de 4 días
		}, {timeout: 5000, maximumAge:10*60*1000});
	}
	
	// INICIALIZACIÓN CUESTIONARIO
	// variables en localStorage
	// timestampPrimeraSesion: timestamp de la primera vez que utilizó el explorador (o que pulsó el botón más tarde)
	// cuestionarioNo: no mostrar más el cuestionario
	// si no había, guardo timestamp primera sesión
	if (!localStorage.getItem('timestampPrimeraSesion'))
		localStorage.setItem('timestampPrimeraSesion', Date.now());
	// detecto si debo mostrar el cuestionario en la sesión
	const gapinicio = Date.now() - Number(localStorage.getItem('timestampPrimeraSesion'));
	if (gapinicio > config.interSessionQGap && !localStorage.getItem('cuestionarioNo')) {
		Sesion.ponerAlertaCuestionario = true; // puede ponerse el cuestionario en la sesión
		Sesion.inicioSesion = Date.now(); // guardo inicio sesión
	}
	else
		Sesion.ponerAlertaCuestionario = false;
}


/////////////////////
// CONFIGURACIÓN SOLR
/////////////////////
function configurarSolr() {
	//console.group("Inicialización motor de texto");
	console.time("Configuración motor de texto");
	
	// inicializo Solr
	Solr = new TextEngine(config.solrConfig.path + config.solrConfig.suggestHandler,
		config.solrConfig.path + config.solrConfig.selectHandler);
	Solr.test()
		.then(() => {
			// OK
			console.info("Motor de texto funciona");						
			console.timeEnd("Configuración motor de texto");
			// muestro la entrada de los lugares
			renderEntradaLugares(true);
		})
		.catch(messageError => {			
			// log del error
			console.error(messageError);
			console.timeEnd("Configuración motor de texto");			
			// pongo Solr a null y escondo la entrada de los lugares
			Solr = null;
			renderEntradaLugares(true);			
		});
}


////////////////////////
// ERROR PROVEEDOR DATOS
////////////////////////
function errorProveedorDatosIrrecuperable(messageError) {
	// ya no tiene sentido pedir datos
	Sesion.errordataprov = true;
	
	// configuro un modal para avisar de que no se puede explorar el inventario	
	configurarModal( { vertcent: true, nofooter: true }, 
		getLiteral(dict.errorEndpointTitle), getLiteral(dict.errorEndpointText), null);
	
	// el modal ya se creó al inicializar la aplicación, por lo que no hace falta esta llamada:
	//mimodal = new bootstrap.Modal(document.getElementById('mimodal'));
		
	// si no está mostrado lo hago
	if (!document.querySelector('.modal.show'))
		mimodal.show();

	// quito temporizador
	finActualizarMapa();
}



///////////////////
// CARGA DE LA URL
// la función clave
///////////////////
function cargarURL(inicial) {
	//console.warn(window.location);	
	// 1) elimino alguna info del estado de la sesión
	delete Sesion.estado.path;
	delete Sesion.estado.etid;
	Sesion.estado.oifns = {}; // otros ifns del dashboard de parcelas
		// => lo muevo sólo al caso mapa
	delete Sesion.zoom; // no es estado, pero lo borro para evitar desajustes

	// preinicializo configuración del mapa y asigno colores iniciales
	if (inicial) {
		// inicializo la configuración del mapa por defecto
		for (let mappar in config.mapConfig)
			Sesion.estado[mappar] = config.mapConfig[mappar][0];
		Sesion.estado.ifn = config.ifnVersions[config.ifnVersions.length-1];
		// asigno colores iniciales aleatoriamente
		for (let ind = 0 ; ind < config.maxTaxonFilters; ind++) {
			const cols = config.coltxinds.filter(col => !Sesion.taxonesColores.includes(col));
			const nind = Math.floor(Math.random() * cols.length);
			Sesion.taxonesColores.push(cols[nind]);
		}
	}

	// 2) actualizo el estado de la sesión a partir de la URL	
	// parseo del querystring de la URL
	const urlParams = new URLSearchParams(window.location.search);
	// actualizo localización si hay localización y está bien
	// en otro caso no borro la localización que hubiera
	const cadloc = urlParams.get('loc');
	if (cadloc) {
		const loc = string2loc(cadloc);
		if (loc) 
			Sesion.estado.loc = loc;
	}
	
	// obtengo el path y valido
	const pathels = window.location.pathname.split("/");
	switch(pathels[1]) { // sólo considero el elemento 1 del path (el 0 será "")
		case "map":
			Sesion.estado.path = pathels[1];
			// extraigo la configuración del mapa de parámetros "mapType", "mapDetails", "plots"
			// valores válidos los de config.mapConfig (y valor por defecto el 0)
			for (let mappar in config.mapConfig) {
				const val = urlParams.get(mappar);
				const valdef = config.mapConfig[mappar][0];
				Sesion.estado[mappar] = (val && config.mapConfig[mappar].includes(val))? val : valdef;
				// conversión de "true" y "false"
				if (Sesion.estado[mappar] === "true")
					Sesion.estado[mappar] = true;
				else if (Sesion.estado[mappar] === "false")
					Sesion.estado[mappar] = false;
			}
			// extraigo versión del ifn
			if (urlParams.get('ifn') != null && config.ifnVersions.includes(urlParams.get('ifn')))
				Sesion.estado.ifn = urlParams.get('ifn');
			else // última versión
				Sesion.estado.ifn = config.ifnVersions[config.ifnVersions.length-1];
			break;
		case "plot": // dashboard parcelas
			if (pathels[2]) {
				// extraigo la iri de la parcela
				// NUEVO: quito "&..." espúreo
				const piri = expandPrefix(pathels[2], config.prefixes).split("&")[0];
				if (piri.startsWith(config.prefixes.pifn2) || piri.startsWith(config.prefixes.pifn3) || piri.startsWith(config.prefixes.pifn4)) {
					// si piri es válida...
					Sesion.estado.path = pathels[1];
					Sesion.estado.etid = piri;
					// me guardo los ifns del dashboard					
					for (const ifnev of config.ifnVersions) {
						if (urlParams.get(ifnev) === 'true')
							Sesion.estado.oifns[ifnev] = true;					
					}
					break;
				}
			}
			// si no hay una iri buena de parcela, voy al caso por defecto			
		default:	// si no hay path o no es válido voy al mapa y a volar
			Sesion.estado.path = "map";
			break;
	}
	// en modo mapa incluyo filtros de taxones si están presentes (Y SON COMPATIBLES) o si es el caso inicial (antes de cargar las especies)
	if (Sesion.estado.path === "map") {
		Sesion.taxones = []; // inicializo
		Sesion.taxonesCorrExpuris = [];
		if (urlParams.get('tx')) {
			let taxones = urlParams.getAll('tx'); // devuelve un array (puede haber múltiples valores)
			// inicializo un objeto para evitar repeticiones
			let objt = {};
			for (let tx of taxones) {
				// expando el taxón con el prefijo (si es que lo hay)
				const taxon = expandPrefix(tx, config.prefixes);
				// caso inicial, guardo el taxón sin comprobar existencia (no puedo saberlo)
				if (inicial)
					objt[taxon] = true;
				else {
					// compruebo existencia del taxón y además mapeo a la versión del inventario
					// esto quiere decir que si tuviéramos un taxón del ifn3 y el inventario ifn2 habrá que buscar una alternativa
					// esto podrá ser consecuencia de una URL inicial mal generada (caso raro) o más bien por el cambio de edición de inventario
					//console.warn("Tx inicial: " + taxon);
					const txmap = correspondenciaTaxon(taxon, Sesion.estado.ifn);
					if (txmap)
						objt[txmap] = true;
					//console.warn("Tx destino (" + Sesion.estado.ifn + "): " + txmap);
				}			
			}
			// guardo en la sesión evitando superar el número máximo de filtros de taxones
			for (let taxon in objt) {
				if (Sesion.taxones.length < config.maxTaxonFilters) {
					Sesion.taxones.push(taxon);
					if (!inicial)
						Sesion.taxonesCorrExpuris.push( correspondenciaTaxonMulti(Datos.taxones[taxon].expuris, "ifn3") );
				}
			}
		}
		// extraigo taxonesFiltros
		const cadtxfilt = urlParams.get('txfilt');
		if (cadtxfilt) {
			try {
				// recompongo la cadena de taxonesFiltros (con control de errores en el parse)
				const cadtxfiltext = JSONCrush.uncrush(decodeURIComponent(cadtxfilt));
				if (cadtxfiltext !== encodeURIComponent(JSONCrush.crush(JSON.stringify(Sesion.taxonesFiltros)))) {
					// recupero el objeto auxiliar
					const aux = JSON.parse(cadtxfiltext);
					// reconstruyo Sesion.taxonesFiltros a partir de aux
					Sesion.taxonesFiltros = {};
					if (aux[config.treeUri])
						Sesion.taxonesFiltros[config.treeUri] = aux[config.treeUri];
					for (const tx of Sesion.taxones) {
						if (aux[tx])
							Sesion.taxonesFiltros[tx] = aux[tx];
					}					
				}
			} catch(e) {
				console.error("Error del txfilt de la URL: " + e);
			}
		}
		else
			Sesion.taxonesFiltros = {}; // inicializo
	}
	
	// NUEVO: reajusto la URL si hace falta
	if (window.location !== obtenerURL())
		history.replaceState(Sesion.estado, "", obtenerURL());

	//3) ajusto vista según el estado del path cargado
	switch(Sesion.estado.path) {
		case "map":				
			// desbloqueo actualizaciones del mapa 
			Sesion.actualizandoMapa = false;			
			// pongo vista mapa
			vistaMapa(true);
			// ajusto radio con la versión del IFN
			if (document.getElementById(Sesion.estado.ifn))
				document.getElementById(Sesion.estado.ifn).checked = true;
			
			// actualizo título de la página
			document.title = getLiteral(dict.map) + ' - ' + getLiteral(dict.title);
			// propiedades meta
			document.querySelector("meta[property='og:title']").setAttribute('content', document.title);
			document.querySelector("meta[property='og:description']").setAttribute('content', 'Navigating the map of Forest Explorer.');		
						
			// si no es la llamada inicial hago más cosas...
			if (!inicial) {			
				// visualización filtros de taxón
				visualizarFiltrosTaxon();
						
				// actualización colores y popups teselas forestales
				const turis = Object.keys(Sesion.tesPintadas);
				ajustarColorTeselas(turis, false);
				ajustarPopupsTeselas(turis, false);
				
				// actualización colores y popups parcelas
				const puris = Object.keys(Sesion.parPintadas);
				ajustarColorParcelas(puris);
				ajustarPopupsParcelas(puris);
				
				// actualización colores y popups árboles
				const pauris = Object.keys(Sesion.parcarbPintadas);
				ajustarColorArboles(pauris);
				ajustarPopupsArboles(pauris);
				
				// actualización colores y popups provincias
				ajustarColorProvincias();
				ajustarPopupsProvincias();

				// centro el mapa en la localización de la sesión
				// esto dispara un evento de mapa movido, disparando su actualización en MODO MAPA
				Mimapa.setView([Sesion.estado.loc.lat, Sesion.estado.loc.lng], Sesion.estado.loc.z);
		
				// incluyo esto para que el mapa se reajuste
				// parece que así se resuelve el problema que había al pasar de recurso a mapa
				// tengo que poner esto después del setview, en otro caso no reacciona al setview
				Mimapa.invalidateSize();
			
				// tour inicial
				if (!localStorage.getItem('tourCompletado'))
					lanzarTour();
			}
			break;
		case "plot":
			// bloqueo actualizaciones del mapa
			Sesion.actualizandoMapa = true;			
			// quito vista mapa
			vistaMapa(false);
			
			// actualizo título de la página
			document.title = getLiteral(dict.plot) + ' ' + uriToLiteral(Sesion.estado.etid, "plot-");
			// propiedades meta
			document.querySelector("meta[property='og:title']").setAttribute('content', document.title);
			document.querySelector("meta[property='og:description']").setAttribute('content', 'Page of plot '+Sesion.estado.etid+' in Forest Explorer.');

			// vamos al dashboard de la parcela
			if (!inicial)
				dashboardParcela();
			break;
		// TODO página de tesela
		// TODO página de especie
		// TODO página de árbol
	} // fin del switch
}
function vistaMapa(esmapa) {
	// ajuste propiedad meta de la url
	document.querySelector("meta[property='og:url']").setAttribute('content', window.location.href);
	if (esmapa) {
		// actualizo viewport (escala fijada)
		document.getElementById("miviewport").setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, shrink-to-fit=no");
		
		// escondo mipagina y muestro mapa
		document.getElementById("mipagina").innerHTML = ''; 
		document.getElementById("mipagina").classList.add("d-none");
		document.getElementById("mimapa").classList.remove("d-none");
	}
	else {
		// actualizo viewport (puede ampliarse)
		document.getElementById("miviewport").setAttribute("content", "width=device-width, initial-scale=1, shrink-to-fit=no");

		// escondo mapa y muestro mipagina
		document.getElementById("mipagina").innerHTML = ''; 
		document.getElementById("mipagina").classList.remove("d-none");
		document.getElementById("mimapa").classList.add("d-none");
	}
}


//////////////////////////////////////////////
// GENERACIÓN DE LA URL A PARTIR DE LA SESIÓN
//////////////////////////////////////////////
function obtenerURL() { // devuelve la URL a partir de Sesion.estado
	// preparo la URL base con el path de Sesion.estado.path
	const { protocol, host } = window.location;
	let url = protocol + '//' + host + '/' + Sesion.estado.path;
	// caso etid (segunda variable de path)
	if (Sesion.estado.etid) 
		url += '/' + applyPrefix(Sesion.estado.etid, config.prefixes);
	// preparo searchpars
	const searchpars = [];
	
	// CASO MAPA
	if (Sesion.estado.loc && Sesion.estado.path === "map")
		searchpars.push('loc=' + loc2string(Sesion.estado.loc));
	if (Sesion.estado.path === "map") {
		// configuración del mapa				
		for (let mappar in config.mapConfig) 
			searchpars.push(mappar + '='+Sesion.estado[mappar]);	
		// configuración de la versión del ifn
		searchpars.push('ifn='+Sesion.estado.ifn);
		
		//searchpars.push('layer='+Sesion.estado.layer);
	}
	if (Sesion.taxones && Sesion.estado.path === "map") {
		for (let taxon of Sesion.taxones) {
			// aplico prefijo
			const tx = applyPrefix(taxon, config.prefixes);
			searchpars.push('tx=' + tx);
		}
	}
	// incluyo txfilt
	if (Sesion.estado.path === "map" && Object.keys(Sesion.taxonesFiltros).length > 0) {
		const cadtxfilt = encodeURIComponent(JSONCrush.crush(JSON.stringify(Sesion.taxonesFiltros)));
		searchpars.push('txfilt='+cadtxfilt);
	}
	
	// CASO DASHBOARD
	if (Sesion.estado.path === "plot") {
		for (const ifnev in Sesion.estado.oifns)
			searchpars.push(ifnev + '=true');
	}
		
	// incluyo los searchpars
	for (let i=0; i<searchpars.length; i++) {
		const sep = i==0? '?' : '&';
		url += sep + searchpars[i];
	}
	return url;
}


export { Sesion, Datos, Layers, Crafts, Solr, cargarURL, obtenerURL };